import { memo, useContext, useEffect, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  updateMissions,
  setMissions,
  getIsMissionsUpdatesPaused,
  getMissionsUpdatesStorage,
  resetMissionsUpdatesStorage,
  fetchMissions,
} from "Slices/missionsPage";
import { getCurrentFleetId } from "Slices/missionPlanner";
import {
  MISSIONS_WS,
  WS_SPINNER_LOGS,
  MISSIONS_VEHICLE_STATE_WS,
} from "Utils/constants";
import { useFMSWebsocket, OnlineStatusesData } from "Utils/webSocketUtils";
import { WebsocketLoaderContext } from "Pages/AuthorizedApp/WebsocketLoaderController";
import { FMSLogger } from "FMSLogger";
import {
  setVehiclesStateData,
  updateVehicle,
  updateVehicleOnlineStatuses,
} from "Slices/vehiclesState";
import { getCurrentSemanticMapId } from "Slices/appState";

const wsSpinnerLogs = FMSLogger.byPrefix(`${WS_SPINNER_LOGS}:LiveMissions`);

const VEHICLE_STATE_FIELDS = [
  "t_drive_mode",
  "t_web_status",
  "t_vehicle_status",
];

export const LiveMissionsController = memo(() => {
  const currentFleetId = useSelector(getCurrentFleetId);
  const currentSemanticMapId = useSelector(getCurrentSemanticMapId);

  const isMissionsUpdatesPaused = useSelector(getIsMissionsUpdatesPaused);
  const missionsUpdatesStorage = useSelector(getMissionsUpdatesStorage);
  const dispatch = useDispatch();

  const setIsWSConnecting = useContext(WebsocketLoaderContext);

  useLayoutEffect(
    () => () => {
      wsSpinnerLogs.debug("unmount component spinner off");
      setIsWSConnecting?.(false);
    },
    []
  );

  useEffect(() => {
    if (currentFleetId) {
      wsSpinnerLogs.debug("new fleet id spinner on");
      setIsWSConnecting?.(true);
    } else {
      wsSpinnerLogs.debug("no fleet id spinner off");
      setIsWSConnecting?.(false);
    }
  }, [currentFleetId]);

  useFMSWebsocket({
    url: "/fleets/mission_queue/",
    subscriptionData: currentFleetId,
    loggerFlag: MISSIONS_WS,
    getDataForSubscribe: (subscriptionId) => {
      wsSpinnerLogs.debug("Subscribing to fleet:", subscriptionId);
      return {
        fleet_ids: [subscriptionId],
      };
    },
    getDataForUnsubscribe: (subscriptionId) => {
      wsSpinnerLogs.debug("Unsubscribing from fleet:", subscriptionId);
      return {
        fleet_ids: [subscriptionId],
      };
    },
    handleInitialMessage: (data) => {
      wsSpinnerLogs.debug("create message spinner off", data);
      setIsWSConnecting?.(false);
      // Process initial data from websocket
      if (typeof data?.missions === "object" && Array.isArray(data.missions)) {
        dispatch(setMissions(data.missions));
      } else if (data) {
        dispatch(updateMissions(data));
      }
    },
    handleMessage: (data) => {
      if (typeof data?.missions === "object") {
        dispatch(updateMissions(data.missions));
      } else {
        dispatch(updateMissions(data));
      }
    },
  });

  useFMSWebsocket({
    url: "/vehicles/state/",
    subscriptionData: currentSemanticMapId,
    loggerFlag: MISSIONS_VEHICLE_STATE_WS,
    getDataForSubscribe: (subscriptionId) => ({
      semantic_map_ids: [subscriptionId],
      fields: VEHICLE_STATE_FIELDS,
    }),
    getDataForUnsubscribe: (subscriptionId) => ({
      semantic_map_ids: [subscriptionId],
    }),
    handleInitialMessage: (data) => {
      dispatch(setVehiclesStateData(data));
    },
    handleMessage: (data: VehicleState) => {
      dispatch(updateVehicle(data));
    },
    handleOnlineStatuses: (data: OnlineStatusesData) => {
      dispatch(updateVehicleOnlineStatuses(data));
    },
  });

  // get mission queue if filters has been changed
  useEffect(() => {
    if (!currentFleetId) return;
    dispatch(
      fetchMissions({
        currentFleetId,
      })
    );
  }, [currentFleetId]);

  // update missions from storage
  useEffect(() => {
    if (
      isMissionsUpdatesPaused !== false ||
      missionsUpdatesStorage.length === 0
    )
      return;

    missionsUpdatesStorage.forEach((update) => {
      dispatch(updateMissions(update));
    });
    dispatch(resetMissionsUpdatesStorage());
  }, [isMissionsUpdatesPaused, missionsUpdatesStorage]);

  return null;
});
